import { createSlice } from "@reduxjs/toolkit";
import { getScrollTopById } from "../../common/utils/scroll";
import { ValidationError } from "../../common/hooks/usePydanticErrorsFormat";
import _ from "lodash";
import { Tender } from "../tender_page/tender.module";
import { Site } from "../sites/sites.module";
import { CurrencyCodeIso } from "../../common/constants/currency";
import { PayoutMethod } from "../../common/constants/payout_methods";
import { PricingStepperType } from "../pricing_request/hooks/usePricingStepper";
import { CCOGermanyCPSubFormValues } from "../../common/BuildCurtailmentObjectsComponents/germany/CCOGermanyCPSubForm";
import { CCOFranceCPSubFormValues } from "../../common/BuildCurtailmentObjectsComponents/france/CCOFranceCPSubForm";
import { PricingAddOn } from "../pricing_request/PricingRequestStepper.slice";

interface ClickabilityParams {
	granularity: string;
	click_count: number;
}

export interface PricingRunSummaryMetric {
	value: number;
	percent?: number;
}
export type SummaryNames =
	| "AlphaProvision"
	| "BetaProvision"
	| "AlphaIndexMargin"
	| "BetaMargin"
	| "AlphaIndex"
	| "AlphaIndexClient"
	| "BetaClient"
	| "Beta";

export interface PricingRunSummary {
	P50: PricingRunSummaryMetric;
	Proxy: PricingRunSummaryMetric;
	LF: PricingRunSummaryMetric;
	Cross: PricingRunSummaryMetric;
	XBC: PricingRunSummaryMetric;
	XPC: PricingRunSummaryMetric;
	XDPC: PricingRunSummaryMetric;
	P50_PROV: PricingRunSummaryMetric;
	Shape: PricingRunSummaryMetric;
	BidAskShape: PricingRunSummaryMetric;
	GOO: PricingRunSummaryMetric;
	Alpha: PricingRunSummaryMetric;
	Beta: PricingRunSummaryMetric;
	BetaClient: PricingRunSummaryMetric;
	BetaFix: PricingRunSummaryMetric;
	Provision: PricingRunSummaryMetric;
	AlphaProvision: PricingRunSummaryMetric;
	BetaProvision: PricingRunSummaryMetric;
	INDIC: PricingRunSummaryMetric;
	Quantity: PricingRunSummaryMetric;
	CM: PricingRunSummaryMetric;
	AlphaBaseloadMargin: PricingRunSummaryMetric;
	AlphaIndexMargin: PricingRunSummaryMetric;
	BetaMargin: PricingRunSummaryMetric;
	AlphaClient: PricingRunSummaryMetric;
	IndicClient: PricingRunSummaryMetric;
	FixingDateTime: PricingRunSummaryMetric;
	BaseloadPrice: PricingRunSummaryMetric;
	BaseloadMid: PricingRunSummaryMetric;
	FixingCommercialMargin: PricingRunSummaryMetric;
	FixPrice: PricingRunSummaryMetric;
	Margin_PROV: PricingRunSummaryMetric;
	GOO_Cross: PricingRunSummaryMetric;
	GOO_Adj: PricingRunSummaryMetric;
	GOO_Price: PricingRunSummaryMetric;
	SetupFee: PricingRunSummaryMetric;
	ImbalanceCostProvision: PricingRunSummaryMetric;
	ProfileCostProvision: PricingRunSummaryMetric;
	DeltaProfileCostProvision: PricingRunSummaryMetric;
	ShapeProvision: PricingRunSummaryMetric;
	ShapeBidAskProvision: PricingRunSummaryMetric;
	FixingGooCommercialMargin: PricingRunSummaryMetric;
	FinalGooPrice: PricingRunSummaryMetric;
	HedgeRatioPercent: PricingRunSummaryMetric;
	TotalPowerMargin: PricingRunSummaryMetric;
	TotalGOOMargin: PricingRunSummaryMetric;
	AlphaIndex: PricingRunSummaryMetric;
	AlphaIndexClient: PricingRunSummaryMetric;
	LivePricingWasUsed?: boolean;
	BlockShape?: PricingRunSummaryMetric;
	ProductionVariabilityBidAsk?: PricingRunSummaryMetric;
	EnergyBidAsk?: PricingRunSummaryMetric;
	CreditCosts?: PricingRunSummaryMetric;
}

export interface PricingRun {
	id: number;
	status: PricingRunStatus;
	start_execution: string;
	end_execution: string;
	validity_date: string;
	pricing_run_date: string;
	pricing_id: number;
	summary?: Partial<PricingRunSummary>;
	computed_status: PricingRunStatus;
	status_comment?: string;
	input_params_s3_filename?: string;
	margin?: number;
	error: string;
	is_pb_error: boolean;
	updated_at: string;
}

export enum PricingRunStatus {
	UNDER_PRICING = "UNDER_PRICING",
	FAILED = "FAILED",
	TIMED_OUT = "TIMED_OUT",
	UNDER_REVIEW = "UNDER_REVIEW",
	INDICATIVE = "INDICATIVE",
	FIRM_REQUESTED = "FIRM_REQUESTED",
	FIRM = "FIRM",
	UNDER_OFFER = "UNDER_OFFER",
	WON = "WON",
	LOST = "LOST",
	CANCELLED = "CANCELLED",
	UNDER_BOOKING = "UNDER_BOOKING",
	BOOKED = "BOOKED",
	BOOKING_FAILED = "BOOKING_FAILED",
	BUILDING_CURTAILMENT_DE = "BUILDING_CURTAILMENT_DE",
	NO_PROXY = "NO_PROXY",
}

export enum PricingStatus {
	UNDER_PRICING = "UNDER_PRICING",
	FAILED = "FAILED",
	TIMED_OUT = "TIMED_OUT",
	UNDER_REVIEW = "UNDER_REVIEW",
	INDICATIVE = "INDICATIVE",
	FIRM_REQUESTED = "FIRM_REQUESTED",
	FIRM = "FIRM",
	UNDER_OFFER = "UNDER_OFFER",
	WON = "WON",
	LOST = "LOST",
	CANCELLED = "CANCELLED",
	UNDER_BOOKING = "UNDER_BOOKING",
	BOOKED = "BOOKED",
	BOOKING_FAILED = "BOOKING_FAILED",
	BUILDING_CURTAILMENT_DE = "BUILDING_CURTAILMENT_DE",
	FIXING_REQUESTED = "FIXING_REQUESTED",
	FIXED = "FIXED",
	FIXED_APPROVED = "FIXED_APPROVED",
	BOOKED_MANUALLY = "BOOKED_MANUALLY",
	NO_PROXY = "NO_PROXY",
}

export interface CurtailmentOutputItem {
	name: string;
	start_date: string;
	end_date: string;
}

export interface CurtailmentOutput {
	errors: string[];
	success: CurtailmentOutputItem[];
}

export enum TenderStatus {
	NEW = "NEW",
	TODO = "TODO",
	ONGOING = "ONGOING",
	TO_BE_CHECKED = "TO_BE_CHECKED",
	PRICING_DONE = "PRICING_DONE",
	UNDER_OFFER = "UNDER_OFFER",
	WON = "WON",
	BOOKED = "BOOKED",
	LOST = "LOST",
	CLOSED = "CLOSED",
}

export interface Field {
	required: boolean;
	label: string | Array<string>;
	default_label?: string;
	name?: string;
	prefill?: string;
	default_value?: string;
	display_name?: string;
	title: string;
	type: "number" | "text" | "group" | "date";
	component: string;
	data_url?: string;
	is_array: boolean;
	disabled?: boolean;
	hidden?: boolean;
	selectable_values?: { value: string; label: string }[];
	must_be_positive: boolean;
	cascade_mapping?: any;
}

export interface FieldGroup {
	type?: "group";
	fields: Field[][];
	required?: boolean | string;
	label?: string | Array<string>;
	default_label?: string;
	default_value?: string;
	name?: string;
	prefill?: string;
	title?: string;
	component?: string;
	display_name?: string;
	is_array?: boolean;
	disabled?: boolean | string;
	hidden?: boolean;
	selectable_values?: { value: string; label: string }[];
	must_be_positive?: boolean;
	cascade_mapping?: any;
}

export interface Action {
	action: string;
	is_unique_by_group: boolean;
	is_unique_by_pricing_type?: boolean;
	is_unique_by_country?: boolean;
	is_unique_by_product_family?: boolean;
	by_site_or_portfolios: boolean;
	display_name: string;
	endpoint: string;
	method: "PATCH" | "POST";
	fields: { [key: string]: Field | FieldGroup };
	filters?: string;
	confirmation_modal?: {
		title: string;
		content: string;
		for_status?: string;
	};
	is_batchable?: boolean;
	redirection_url?: string;
	force?: boolean;
}

export interface Party {
	name: string;
	id?: number;
	party_id: number;

	short_name: string;
}

export interface Book {
	name: string;
	id?: number;
	geco_id: number;
	portfolio_prefix?: string;
}

export interface PricingParty {
	id: number;
	party: Party;
	percentage: number;
	mirror_book?: Book;
}

export interface Technology {
	name: string;
	id: number;
}

export interface Timeserie {
	best_off_tourbillon_name: string;
	cleaned_realized_tourbillon_name: string;
	outliers_tourbillon_name: string;
	proxy_tourbillon_name: string;
	realized_tourbillon_name: string;
	error: string;
	warning: string;
}

export interface Pricing {
	id: number;
	pricing_type_id: number;
	alpha?: boolean;
	has_alpha?: boolean;
	beta?: boolean;
	has_beta?: boolean;
	alpha_and_beta?: boolean;
	apply_set_up_fees?: boolean;
	aggregated_pricing_type_name: string;
	pricing_type: {
		name: string;
		long_name: string;
		display_name: string;
		geco_name: string;
		is_baseprod?: boolean;
		family: PricingStepperType;
	};
	commodity_pricing_type: {
		name: string;
		long_name: string;
		geco_name: string;
		is_baseprod?: boolean;
	};
	site_or_portfolio?: string;
	start_date: string;
	end_date: string;
	pricing_date: string;
	margin?: number;
	clickability_params: ClickabilityParams;
	granularity: string;
	tender_id: number;
	result_option: string;
	selected_result_option: string;
	portfolio_id?: number;
	portfolio?: {
		name: string;
		id: number;
	};
	site_pricings: any[];
	base_capacity_volume_percentage?: number;
	price_max_validity?: "string";
	pricing_runs: PricingRun[];
	status: PricingStatus;
	actions: Action[];
	status_comment: string;
	delivery_period: string;
	geco_contract_id?: number | null;
	installed_capacity: number;
	goo_price?: number;
	party?: Party;
	booking_outputs?: string;
	curtailment_built_ouputs: CurtailmentOutput;
	validity_date: string;
	pricing_group_id: number;
	tourbillon_ref?: string;
	isNewUpdate?: boolean;
	is_real_time?: boolean;
	updated_at: string;
	mirror_book: string;
	party_or_mirror: string;
	isStatusUpdating?: boolean;
	chapter51_type?: string;
	chapter51_number_of_hours?: number;
	technology_id?: number;
	techno?: Technology;
	goo_power_adjustment?: number;
	parties?: any[];
	timeserie?: Timeserie;
	balancing_group: string;
	balancing_group_label: string;
	marketing_type?: string;
	isSelected?: boolean;
	isExpanded?: boolean;
	is_financial?: boolean;
	isLoadingRuns?: boolean;
	portfolio_tourbillon_ref_breakdown: Record<string, string>;
	portfolio_asset_id_breakdown: Record<string, string>;
	country_iso2_code?: string;
	tso_id?: number;
	tso_name?: string;
	isHidden?: boolean;
	tender_name?: string;
	currency?: CurrencyCodeIso;
	negative_price_value?: number;
	negative_price_index?: {
		geco_id: number;
		name: string;
		orchestrade_id: number;
	};
	negative_price_threshold?: number;
	negative_price_operator?: string;
	negative_price_nb_consecutive_blocks?: number;
	has_negative_price_conditions?: boolean;
	curtailability_pricing_index?: {
		geco_id: number;
		name: string;
		orchestrade_id: number;
	};
	curtailability_forward_threshold?: number;
	curtailability_forward_operator?: string;
	curtailability_price_nb_consecutive_blocks?: number;
	has_curtailability_forward_conditions?: boolean;
	is_nodal: boolean;
	countries_list?: string[];
	payout_method?: PayoutMethod;
	cap_value?: number;
	floor_value?: number;
	payout_method_intraday?: PayoutMethod;
	cap_value_intraday?: number;
	floor_value_intraday?: number;
	settlement_period_label?: string;
	cco_build_input?: CCOGermanyCPSubFormValues | CCOFranceCPSubFormValues;
	provision_type: string;
	pricing_add_ons: PricingAddOn[];
	spot_market: string;
}

interface PricingListState {
	pricings: Pricing[];
	hasNextPage: boolean;
	latestUpdatedAt: undefined;
	latestPricingId: undefined;
	addedPricingCount: number;
	pricingTso: {
		defaultValue?: string;
		warning?: string;
		options: { label: string; value: string }[];
	};
	pricingOptions: {
		defaultValue?: string;
		warning?: string;
		options: { label: string; value: string }[];
	};
	loader: {
		pricings: boolean;
		export: boolean;
		transition: boolean;
		pricingRuns: Record<number, boolean>;
		parties: boolean;
		tender: boolean;
		pricingTso: boolean;
		pricingOptions: boolean;
	};
	errors: {
		pricings?: string;
		export?: any;
		transition?: string | ValidationError;
		parties?: string;
		graphs?: any;
	};
	pricingRuns: Record<number, PricingRun[]>;
	parties: Party[];
	currentTender: Tender | undefined;
	graphTypes: { id: number; ts_name: string }[] | undefined;
	sitePricings: { site: Site }[];
}

const initialState: PricingListState = {
	pricings: [],
	hasNextPage: true,
	latestUpdatedAt: undefined,
	latestPricingId: undefined,
	addedPricingCount: 0,
	pricingTso: { defaultValue: undefined, options: [] },
	pricingOptions: { defaultValue: undefined, options: [] },
	loader: {
		pricings: false,
		export: false,
		transition: false,
		pricingRuns: {},
		parties: false,
		tender: false,
		pricingTso: false,
		pricingOptions: false,
	},
	errors: {},
	pricingRuns: {},
	parties: [],
	currentTender: undefined,
	graphTypes: undefined,
	sitePricings: [],
};

export type LoaderName =
	| "pricings"
	| "export"
	| "transition"
	| "tender"
	| "pricingTso"
	| "pricingOptions";

export const pricingListSlice = createSlice({
	name: "pricingList",
	initialState,
	reducers: {
		resetPricings: (state) => {
			state.pricings = [];
			state.pricingRuns = {};
			state.loader.pricingRuns = {};
			state.latestUpdatedAt = undefined;
			state.latestPricingId = undefined;
			state.hasNextPage = true;
			state.addedPricingCount = 0;
		},
		getPricingSuccess: (state, action) => {
			const {
				pricings,
				latestUpdatedAt,
				latestUpdatedAtParam,
				hasNextPage,
			} = action.payload;
			const scrollTop = getScrollTopById("pricing-table-container");
			if (latestUpdatedAtParam) {
				const addedPricings: Pricing[] = [];
				pricings.forEach((pricing: Pricing) => {
					const index = state.pricings.findIndex(
						(p: Pricing) => p.id == pricing.id
					);
					if (index !== -1) {
						state.pricings[index] = pricing;
					} else {
						addedPricings.push(pricing);
					}
				});

				if (addedPricings.length > 0) {
					if (scrollTop != undefined && scrollTop < 100) {
						const sortedState = [
							...addedPricings,
							...state.pricings,
						].sort((a: Pricing, b: Pricing) => b.id - a.id);
						state.pricings = sortedState.slice(0, 20);
						// Trigger a reset of paging count
						state.addedPricingCount = -1;
					} else {
						// Trigger snackbar to reload
						state.addedPricingCount = addedPricings.length;
					}
				}
			} else {
				state.pricings = _.uniqBy(
					_.orderBy(
						[...state.pricings, ...pricings],
						[
							"pricing_group_id",
							"id",
							(pricing) => new Date(pricing.updated_at).getTime(),
						],
						["desc", "desc", "desc"]
					),
					"id"
				);
			}
			state.latestUpdatedAt = latestUpdatedAt;
			state.hasNextPage = hasNextPage;
			state.loader.pricings = false;
		},
		getPricingError: (state, action) => {
			state.errors.pricings = action.payload;
			state.loader.pricings = false;
		},
		getCancelledPricingsError: (state, action) => {
			state.errors.pricings = action.payload;
		},
		transitionError: (state, action) => {
			state.errors.transition = action.payload;
		},
		setLoader: (state, action) => {
			const name: LoaderName = action.payload.name;
			state.loader[name] = action.payload.value;
		},
		setPricingLoadingState: (state) => {
			state.loader.pricings = true;
		},
		setExportLoadingState: (state, action) => {
			state.loader.export = action.payload;
		},
		setExportLoadingError: (state, action) => {
			state.errors.export = action.payload;
		},
		setPricingRunLoader: (state, action) => {
			const { pricingId, isLoading } = action.payload;
			state.loader.pricingRuns[pricingId] = isLoading;
		},
		pricingRunSuccess: (state, action) => {
			const { pricingId, runs } = action.payload;
			if (!state.pricingRuns[pricingId]) {
				state.pricingRuns[pricingId] = [];
			}
			state.pricingRuns[pricingId] = _.uniqBy(
				[...state.pricingRuns[pricingId], ...runs],
				"id"
			);
		},
		resetPricingRun: (state, action) => {
			const { pricingId } = action.payload;
			state.pricingRuns[pricingId] = [];
		},
		updateNewPricingsCount: (state) => {
			state.addedPricingCount = 0;
		},
		getPartiesSuccess: (state, action) => {
			state.parties = action.payload;
			state.loader.parties = false;
		},
		updatePartiesLoader: (state, action) => {
			state.loader.parties = action.payload;
		},
		getPartiesError: (state, action) => {
			state.errors.parties = action.payload;
			state.loader.parties = false;
		},
		setPricingTender: (state, action) => {
			state.currentTender = action.payload;
		},
		updatePricingsTender: (state, action) => {
			if (
				state.currentTender?.id == action.payload.id ||
				state.currentTender == undefined
			)
				state.currentTender = action.payload;
		},
		setPricingsStatusLoading: (state, action) => {
			state.pricings = state.pricings.map((pricing) => {
				if (action.payload.ids.includes(pricing.id)) {
					return {
						...pricing,
						isStatusUpdating: action.payload.status,
					};
				}
				return pricing;
			});
		},
		setPricings: (state, action) => {
			state.pricings = action.payload;
		},
		setPricingTso: (state, action) => {
			state.pricingTso = action.payload;
		},
		setPricingOptions: (state, action) => {
			state.pricingOptions = action.payload;
		},

		getGraphTypesSuccess: (state, action) => {
			state.graphTypes = action.payload;
		},
		getGraphError: (state, action) => {
			state.errors.graphs = action.payload;
		},

		initGraphTypes: (state) => {
			state.graphTypes = undefined;
		},
		getSitePricingsSuccess: (state, action) => {
			state.sitePricings = action.payload;
		},
	},
});

export const {
	getPricingSuccess,
	getSitePricingsSuccess,
	getPricingError,
	setPricingLoadingState,
	resetPricings,
	setExportLoadingState,
	setExportLoadingError,
	transitionError,
	setLoader,
	setPricingRunLoader,
	pricingRunSuccess,
	resetPricingRun,
	updateNewPricingsCount,
	getPartiesSuccess,
	getPartiesError,
	updatePartiesLoader,
	updatePricingsTender,
	setPricingTender,
	getCancelledPricingsError,
	setPricingsStatusLoading,
	setPricings,
	getGraphTypesSuccess,
	getGraphError,
	initGraphTypes,
	setPricingTso,
	setPricingOptions,
} = pricingListSlice.actions;

export default pricingListSlice.reducer;
